import { useMemo } from 'react'
import { Navigate, RouteObject, useRoutes } from 'react-router-dom'
import i18next from 'i18next'

import { ConfiguredEntities } from '@pages/ConfiguredEntities'
import { ConfiguredEntityPage } from '@pages/ConfiguredEntityPage'
import { FormPage } from '@pages/FormPage'

import { ConfigMobileApps } from '@mobilePages/ConfigMobileApps'
import { MobileAppCreateOrEdit } from '@mobilePages/MobileAppCreateOrEdit'
import { ConfigMobileScreens } from '@mobilePages/ConfigMobileScreens'
import { MobileScreenCreateOrEdit } from '@mobilePages/MobileScreenCreateOrEdit'
import { ConfigMobileMaps } from '@mobilePages/ConfigMobileMaps'
import { MobileMapCreateOrEdit } from '@mobilePages/MobileMapCreateOrEdit'
import { ConfigMobileListItems } from '@mobilePages/ConfigMobileListItems'
import { MobileListItemCreateOrEdit } from '@mobilePages/MobileListItemCreateOrEdit'
import { ConfigMobileForms } from '@mobilePages/ConfigMobileForms'
import { MobileFormCreateOrEdit } from '@mobilePages/MobileFormCreateOrEdit'
import { ConfigMobileSearchAssistants } from '@mobilePages/ConfigMobileSearchAssistants'
import { MobileSearchAssistantCreateOrEdit } from '@mobilePages/MobileSearchAssistantCreateOrEdit'
import { ConfigMobileGlobalVariables } from '@mobilePages/ConfigMobileGlobalVariables'
import { ConfigMobileReports, MobileReportDetails } from '@mobilePages/ConfigMobileReports'
import { ConfigStyleSettings } from '@mobilePages/ConfigStyleSettings'
import { DisplayRules } from '@mobilePages/DisplayRules'
import { DisplayRulesCreateOrEdit } from '@mobilePages/DisplayRulesCreateOrEdit'

import { AppLayout } from '@layouts/AppLayout'
import { SwitchRouter } from '@components/SwitchRouter'

import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { useAuth, useControlTitleTabs } from '@hooks'
import { getOptionRoute, transformCommonParametersToValues } from '@helpers'
import { COMMON_PARAMETERS, ELEMENT_TYPE, ENTITY, ROUTES, USER_ROLES } from '@constants'
import { GETControlPanelLineItem } from '@types'

import { useDefaultTimezone } from '../../hooks/useDefaultTimezone'

export const MainRoute = () => {
  useControlTitleTabs()
  const { hasRole } = useAuth()

  let menu: any = undefined
  const isLoadingMenu = false
  let defaultEntitiesBackground: any[] | undefined = undefined

  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  useDefaultTimezone(initialCommonParameters)

  const addDefaultRoutes = (
    defaultEntitiesBackground: any | undefined,
    routes: RouteObject[] = []
  ): RouteObject[] => {
    if (!defaultEntitiesBackground?.length) {
      return []
    }

    if (defaultEntitiesBackground?.length === 1) {
      const { sourceId, title, type, code } = defaultEntitiesBackground[0]
      const path = getOptionRoute({ id: sourceId, title })

      const element = (
        <ConfiguredEntityPage
          entityCode={code}
          entityId={sourceId}
          hideSA={false}
          title={title}
          type={ENTITY.VIEW}
          path={path}
          // For rerender
          key={path}
        />
      )

      routes.push({
        path,
        element,
        children: [
          {
            path: '/' + path + ROUTES.FORM,
            element: <FormPage />,
          },
        ],
      })
    }

    if (defaultEntitiesBackground?.length === 2) {
      const path = getOptionRoute(
        {
          id: defaultEntitiesBackground[0].sourceId,
          title: defaultEntitiesBackground[0].title,
        },
        {
          id: defaultEntitiesBackground[1].sourceId,
          title: defaultEntitiesBackground[1].title,
        }
      )

      routes.push({
        path,
        element: (
          <ConfiguredEntities
            // for rerender
            entities={defaultEntitiesBackground}
            path={path}
          />
        ),
        children: [
          {
            path: '/' + path + ROUTES.FORM,
            element: <FormPage />,
          },
        ],
      })
    }

    return routes
  }

  // Создание роутов для динамически конфигурируемых страниц
  const createConfiguredRoutes = (menu: GETControlPanelLineItem[], routes: RouteObject[] = []) => {
    menu.forEach(menuItem => {
      const path = getOptionRoute(menuItem)

      if (menuItem.elementId && menuItem.elementType === ELEMENT_TYPE.VIEW) {
        routes.push({
          path,
          element: (
            <ConfiguredEntityPage
              entityCode={menuItem?.elementCode || undefined}
              entityId={Number(menuItem?.elementId) || undefined}
              hideSA={false}
              title={menuItem.elementTitle || ''}
              type={ENTITY.VIEW}
              path={path}
              // For rerender
              key={path}
            />
          ),
          children: [
            // TODO открывается форма вместо View, на той же странице в Outlet.
            //  Неизвестно потребуется ли этот функционал после доработки с открытием Form и View через диалоговые окна.
            {
              path: '/' + path + ROUTES.FORM,
              element: <FormPage />,
            },
          ],
        })
      }

      if (menuItem.childrenMenu) {
        createConfiguredRoutes(menuItem.childrenMenu, routes)
      }
    })

    return routes
  }

  const renderByRoles = (roles: USER_ROLES[], routes: RouteObject[]): RouteObject[] => {
    return hasRole(roles) ? routes : []
  }

  const routes: RouteObject[] = useMemo(
    () => [
      ...renderByRoles(
        [USER_ROLES.ADMIN],
        [
          {
            element: <ConfigMobileApps />,
            path: ROUTES.CONFIG_MOBILE_APPS,
            children: [
              {
                path: ROUTES.CONFIG_MOBILE_APPS_CREATE,
                element: <MobileAppCreateOrEdit />,
              },
              {
                path: ROUTES.CONFIG_MOBILE_APPS_EDIT_ID,
                element: <MobileAppCreateOrEdit />,
              },
            ],
          },
          {
            element: <ConfigMobileScreens />,
            path: ROUTES.CONFIG_MOBILE_SCREENS,
            children: [
              {
                path: ROUTES.CONFIG_MOBILE_SCREENS_CREATE,
                element: <MobileScreenCreateOrEdit />,
              },
              {
                path: ROUTES.CONFIG_MOBILE_SCREENS_EDIT_ID,
                element: <MobileScreenCreateOrEdit />,
              },
            ],
          },
          {
            element: <ConfigMobileMaps />,
            path: ROUTES.CONFIG_MOBILE_MAPS,
            children: [
              {
                path: ROUTES.CONFIG_MOBILE_MAPS_CREATE,
                element: <MobileMapCreateOrEdit />,
              },
              {
                path: ROUTES.CONFIG_MOBILE_MAPS_EDIT_ID,
                element: <MobileMapCreateOrEdit />,
              },
            ],
          },
          {
            element: <ConfigMobileListItems />,
            path: ROUTES.CONFIG_MOBILE_LIST_ITEMS,
            children: [
              {
                path: ROUTES.CONFIG_MOBILE_LIST_ITEMS_CREATE,
                element: <MobileListItemCreateOrEdit />,
              },
              {
                path: ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT_ID,
                element: <MobileListItemCreateOrEdit />,
              },
            ],
          },
          {
            element: <ConfigMobileForms />,
            path: ROUTES.CONFIG_MOBILE_FORMS,
            children: [
              {
                path: ROUTES.CONFIG_MOBILE_FORMS_CREATE,
                element: <MobileFormCreateOrEdit />,
              },
              {
                path: ROUTES.CONFIG_MOBILE_FORMS_EDIT_ID,
                element: <MobileFormCreateOrEdit />,
              },
            ],
          },
          {
            element: <ConfigMobileSearchAssistants />,
            path: ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS,
            children: [
              {
                path: ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_CREATE,
                element: <MobileSearchAssistantCreateOrEdit />,
              },
              {
                path: ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_EDIT_ID,
                element: <MobileSearchAssistantCreateOrEdit />,
              },
            ],
          },
          {
            element: <ConfigMobileGlobalVariables />,
            path: ROUTES.CONFIG_MOBILE_GLOBAL_VARIABLES,
          },
          {
            element: <ConfigMobileReports />,
            path: ROUTES.CONFIG_MOBILE_REPORTS,
            children: [
              {
                path: ROUTES.CONFIG_MOBILE_REPORTS_ID,
                element: <MobileReportDetails />,
              },
            ],
          },
          {
            element: <ConfigStyleSettings />,
            path: ROUTES.CONFIG_STYLE_SETTINGS,
          },
          {
            element: <DisplayRules />,
            path: ROUTES.CONFIG_DISPLAY_RULES,
            children: [
              {
                path: ROUTES.CONFIG_DISPLAY_RULES_CREATE,
                element: <DisplayRulesCreateOrEdit />,
              },
              {
                path: ROUTES.CONFIG_DISPLAY_RULES_EDIT_ID,
                element: <DisplayRulesCreateOrEdit />,
              },
            ],
          },
        ]
      ),
      {
        element: (
          <ConfiguredEntityPage
            key={ROUTES.SEARCH}
            path={ROUTES.SEARCH}
            hideSA={false}
            title={i18next.t('searchAssistant.title')}
            type={ENTITY.VIEW}
          />
        ),
        path: ROUTES.SEARCH,
        children: [
          {
            path: '/' + ROUTES.SEARCH + ROUTES.FORM,
            element: <FormPage />,
          },
        ],
      },
      ...(menu?.panel?.lines
        ? menu.panel.lines.map((line: any) => createConfiguredRoutes(line.items)).flat()
        : []),
      ...addDefaultRoutes(defaultEntitiesBackground),
      {
        element: <Navigate replace to={ROUTES.CONFIG_MOBILE_APPS} />,
        path: ROUTES.CHOOSE_CONTEXT,
      },
      ...(((defaultEntitiesBackground ?? []).length === 0 ? [{ path: ROUTES.DEFAULT_PAGE }] : [])),
      // SwitchRouter as last fallback for unmatched routes
      ...(!isLoadingMenu
        ? [
            {
              element: <SwitchRouter defaultEntities={defaultEntitiesBackground} />,
              path: '*',
            },
          ]
        : []),
    ],
    [initialCommonParameters, menu, defaultEntitiesBackground, isLoadingMenu]
  )

  const appLayout = <AppLayout>{useRoutes(routes)}</AppLayout>

  return appLayout
}
